import { css, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'

import AnimatedCodeWrapper from './AnimatedCodeWrapper'
import Button from 'components/Button'
import { PaperPlaneIcon, ShareIcon } from 'components/icons'
import useTemplateId from 'hooks/useTemplateId'
import { useTracking } from 'hooks/useTracking'
import { config } from 'lib/config'
import { INSTALLATION_GUIDES } from 'lib/constants'
import { getSeamlessInstallationGuides } from 'lib/seamlessInstallationGuides'
import { EVENT_NAMES } from 'lib/tracking'
import { formControlReset } from 'styles/reset'
import { Theme } from 'styles/theme'
import { Organization } from 'types/organization'

const Plane = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spaces.space7};
	${({ theme }) =>
		theme.isMobile &&
		css`
			padding: 0;
			margin: 0;
		`};
`

const BottomShadow = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 140px;
	background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fafafa 100%);
	border-radius: ${({ theme }) => theme.borderRadiuses.xl};
	display: flex;
	justify-content: center;
	align-items: end;
`

const ExpandButton = styled(Button)`
	background-color: #ebebeb;
`

const ImageContainer = styled.div`
	width: 48px;
	height: 48px;
	display: flex;
	align-items: center;
`

const Caption = styled.p`
	${({ theme }) => theme.typography.caption}
	margin-bottom: 0;
`

const Subtitle = styled.h2`
	margin-bottom: 0;
	${({ theme }) => theme.typography.captionMedium}
`

const InstallationGuide = styled.ul<{ isCollapsed: boolean }>`
	position: relative;
	display: grid;
	grid-template-columns: ${({ theme }) =>
		`repeat(auto-fit, minmax(min(100%/2 - ${theme.spaces.space5}, max(120px, 100%/4 - calc(${theme.spaces.space5} * 3))), 1fr))`};
	gap: ${({ theme }) => theme.spaces.space5};
	padding: ${({ theme }) => theme.spaces.space3};
	list-style-type: none;
	margin-top: 0;
	${({ isCollapsed }) =>
		isCollapsed &&
		css`
			max-height: 200px;
			overflow-y: hidden;
		`}
`

const guideTileStyles = (theme: Theme) => css`
	border-radius: ${theme.borderRadiuses.lg};
	${formControlReset}
	height: 115px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	background-color: ${theme.colors.light.surfaceDecorative};
	gap: 10px;
	font-size: ${theme.fontSizes.size12};
	font-weight: 600;
	cursor: pointer;
	text-decoration: none;
	color: inherit;

	@media (hover: hover) {
		will-change: box-shadow, background-color;
		transition: box-shadow 150ms ease-out, background-color 150ms ease-out;

		&:hover {
			box-shadow: ${theme.boxShadows.md};
			background-color: ${theme.colors.light.surfaceVariant};
		}
	}
`

const SupportedCompany = styled.span`
	text-align: center;
`

const HelpSection = styled.div`
	display: flex;
	gap: ${({ theme }) => theme.spaces.space5};

	& > button {
		flex-basis: ${({ theme }) => (theme.isMobile ? '100%' : '50%')};
		@media (hover: hover) {
			will-change: box-shadow, background-color;
			transition: box-shadow 150ms ease-out, background-color 150ms ease-out;

			&:hover {
				box-shadow: ${({ theme }) => theme.boxShadows.md};
				background-color: ${({ theme }) => theme.colors.light.surfaceVariant};
			}
		}
	}
`

type Props = {
	organizationId: string
	isPlatformPromptVisible?: boolean
	organizationTracking?: Organization['tracking']
}

function CodeInstallation({ organizationId, isPlatformPromptVisible, organizationTracking }: Props) {
	const theme = useTheme()
	const track = useTracking()
	const [areArticlesCollapsed, setAreArticlesCollapsed] = React.useState(true)
	const articlesListRef = React.useRef<HTMLUListElement>(null)
	const router = useRouter()
	const isSharePage = router.route.startsWith('/share/')
	const { withTemplateId } = useTemplateId()
	const seamlessInstallationsGuides = getSeamlessInstallationGuides(organizationTracking)

	const triggerWidget = () => {
		if ('OpenWidget' in window) {
			window.OpenWidget.call('maximize', { feature: 'form-contact' })
		}
		track(EVENT_NAMES.CONTACT_US_CLICKED, { source: 'Configurator' })
	}

	const handleShareUrl = async () => {
		const shareUrl = withTemplateId(`${config.appURL}/share/${organizationId}`)

		let method = 'share'
		const fallbackToEmail = () => {
			method = 'email'
			window.location.href = `mailto:?subject=Install OpenWidget&body=To add OpenWidget to a website, visit%0A${shareUrl}%0Aand follow the instructions listed there.`
		}

		const shareMessage = `To add OpenWidget to a website, visit\n ${shareUrl}\n and follow the instructions listed there.`

		if (typeof navigator.share !== 'function') {
			fallbackToEmail()
		} else {
			try {
				await navigator.share({ text: shareMessage })
			} catch (error) {
				const triggerFallbackErrors = ['InvalidStateError', 'NotSupportedError']
				if (error instanceof DOMException && triggerFallbackErrors.includes(error.name)) {
					fallbackToEmail()
				}
			}
		}

		track(EVENT_NAMES.SEND_INSTRUCTIONS_CLICKED, { method })
	}

	const handleArticlesExpand = () => {
		setAreArticlesCollapsed(false)
		setTimeout(() => articlesListRef.current?.scrollIntoView({ behavior: 'smooth' }), 0)
	}

	return (
		<Plane>
			<Subtitle>Your OpenWidget is ready! Add it to your website and start helping your customers right away.</Subtitle>
			<AnimatedCodeWrapper organizationId={organizationId} isPrimaryAction={!isPlatformPromptVisible} />
			<Subtitle>Need help with the installation?</Subtitle>
			<HelpSection>
				<Button type="button" kind="secondary" compact onClick={handleShareUrl} icon={<ShareIcon />}>
					Send instructions
				</Button>
				{!theme.isMobile && (
					<Button type="button" kind="secondary" compact onClick={triggerWidget} icon={<PaperPlaneIcon />}>
						Write to us
					</Button>
				)}
			</HelpSection>
			<Caption>Try seamless integration with:</Caption>
			<InstallationGuide isCollapsed={false}>
				{seamlessInstallationsGuides.map(({ name, logo, pluginURL, articleURL }, index) => {
					const content = (
						<>
							<ImageContainer aria-hidden>
								<Image height="48" width="48" src={logo} alt={name} />
							</ImageContainer>
							<SupportedCompany>{name}</SupportedCompany>
						</>
					)

					if (isSharePage) {
						return (
							<li key={index} onClick={() => track(EVENT_NAMES.HELP_LINK_CLICKED, { link: name })}>
								<Link css={guideTileStyles(theme)} href={articleURL} target="_blank" rel="noopener,noreferrer">
									{content}
								</Link>
							</li>
						)
					}

					if (name === 'Google Tag Manager') {
						return (
							<li key={index}>
								<Link
									css={guideTileStyles(theme)}
									href={withTemplateId(`${router.asPath.split('?')[0]}/gtm/sign-in`)}
									shallow
								>
									{content}
								</Link>
							</li>
						)
					}

					return (
						<li key={index}>
							<Link css={guideTileStyles(theme)} href={pluginURL} target="_blank" rel="noopener,noreferrer">
								{content}
							</Link>
						</li>
					)
				})}
			</InstallationGuide>
			<Caption>See how to install OpenWidget with:</Caption>
			<InstallationGuide
				data-testid="installation-guide-wrapper"
				ref={articlesListRef}
				isCollapsed={!!isPlatformPromptVisible && areArticlesCollapsed}
			>
				{INSTALLATION_GUIDES.map(({ name, logo, articleURL }, index) => (
					<li key={index} onClick={() => track(EVENT_NAMES.HELP_LINK_CLICKED, { link: name })}>
						<Link css={guideTileStyles(theme)} href={articleURL} target="_blank" rel="noopener,noreferrer">
							<ImageContainer aria-hidden>
								<Image height="48" width="48" src={logo} alt={name} />
							</ImageContainer>
							<SupportedCompany>{name}</SupportedCompany>
						</Link>
					</li>
				))}
				{isPlatformPromptVisible && areArticlesCollapsed && (
					<BottomShadow>
						<div>
							<ExpandButton compact kind="secondary" onClick={handleArticlesExpand}>
								Show all articles
							</ExpandButton>
						</div>
					</BottomShadow>
				)}
			</InstallationGuide>
		</Plane>
	)
}

export default CodeInstallation
